import type {
  Account,
  AlbumDetail,
  BillingInterval,
  BillingSummary,
  SubscriptionView,
  AlbumItemView,
  AlbumSummary,
  AssetView,
  AuthResult,
  DeviceDetail,
  DeviceMember,
  DeviceSummary,
  DeviceSharing,
  FanoutResult,
  InvitePreview,
  PhotoRecord,
  PhotoText,
  PublicFrame,
  ReconcileSummary,
  Screen,
  ShareInvite,
  SlideshowSettings,
  UploadResult,
} from '@mantelpiece/shared';
import type { PreparedPhoto } from './image';

/** A photo row plus the signed thumbnail URL the contact sheet renders. */
export interface ListedPhoto extends PhotoRecord {
  readonly thumbUrl: string;
}

/** An album as the frame that carries it sees it: not editable, but droppable. */
export interface CarriedAlbum {
  readonly albumId: string;
  readonly label: string;
  readonly ownerName: string;
  readonly publishedAt: number;
  readonly photoCount: number;
}

const SESSION_KEY = 'mantelpiece.session';
/**
 * Which frame this browser's viewer is showing, when it is showing one on the
 * account's authority rather than a display token. Lives beside the session
 * because it means nothing without one, and on the app's origin only — the
 * viewer hostname has neither.
 */
const SHOWN_FRAME_KEY = 'mantelpiece.frame';

export interface StoredSession {
  readonly token: string;
  readonly account: Account;
}

export const loadSession = (): StoredSession | null => {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredSession>;
    if (typeof parsed.token !== 'string' || !parsed.account) return null;
    return { token: parsed.token, account: parsed.account };
  } catch {
    return null;
  }
};

export const saveSession = (session: StoredSession): void => {
  localStorage.setItem(SESSION_KEY, JSON.stringify(session));
};

export const clearSession = (): void => {
  localStorage.removeItem(SESSION_KEY);
  // The chosen frame goes too. It was shown on this account's authority, and
  // leaving it behind would mean the next person to sign in here inherits a
  // pointer to a frame that is not theirs.
  localStorage.removeItem(SHOWN_FRAME_KEY);
};

export const loadShownFrame = (): string | null => {
  try {
    return localStorage.getItem(SHOWN_FRAME_KEY);
  } catch {
    return null;
  }
};

export const saveShownFrame = (id: string): void => {
  try {
    localStorage.setItem(SHOWN_FRAME_KEY, id);
  } catch {
    /* Private browsing. The viewer still works, it just forgets. */
  }
};

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

const request = async <T>(
  path: string,
  init: RequestInit & { token?: string | undefined } = {},
): Promise<T> => {
  const { token, headers, ...rest } = init;
  const response = await fetch(`/v1${path}`, {
    ...rest,
    headers: {
      ...(token ? { authorization: `Bearer ${token}` } : {}),
      ...headers,
    },
  });

  if (response.status === 204) return undefined as T;

  const payload = (await response.json().catch(() => null)) as
    | (T & { error?: string; message?: string })
    | null;

  if (!response.ok) {
    throw new ApiError(
      response.status,
      payload?.error ?? 'unknown',
      payload?.message ?? 'Something went wrong. Try again.',
    );
  }
  return payload as T;
};

const json = (body: unknown, method = 'POST'): RequestInit => ({
  method,
  headers: { 'content-type': 'application/json' },
  body: JSON.stringify(body),
});

const photoForm = (photo: PreparedPhoto, text: PhotoText): FormData => {
  const form = new FormData();
  form.set('meta', JSON.stringify({ ...photo.meta, ...text }));
  form.set('display', photo.display, 'display.jpg');
  form.set('thumb', photo.thumb, 'thumb.jpg');
  return form;
};

export const api = {
  /* -------------------------------------------------------------- account -- */

  signUp: (email: string, password: string, name: string) =>
    request<AuthResult>('/auth/signup', json({ email, password, name })),

  signIn: (email: string, password: string) =>
    request<AuthResult>('/auth/signin', json({ email, password })),

  signOut: (token: string) => request<void>('/auth/signout', { method: 'POST', token }),

  whoAmI: (token: string) => request<{ account: Account }>('/auth/me', { token }),

  rename: (token: string, name: string) =>
    request<{ account: Account }>('/auth/me', { ...json({ name }, 'PATCH'), token }),

  changePassword: (token: string, current: string, next: string) =>
    request<void>('/auth/password', { ...json({ current, next }), token }),

  /* -------------------------------------------------------------- billing -- */

  billing: (token: string) => request<BillingSummary>('/billing', { token }),

  /** Off to Stripe's hosted checkout. The page it answers with is left for. */
  startCheckout: (token: string, interval: BillingInterval, quantity: number) =>
    request<{ url: string }>('/billing/checkout', { ...json({ interval, quantity }), token }),

  /** Stripe's billing portal: card, invoices, cancellation. */
  openBillingPortal: (token: string) =>
    request<{ url: string }>('/billing/portal', { method: 'POST', token }),

  /** Back from checkout before the webhook has landed: ask Stripe directly. */
  syncBilling: (token: string) =>
    request<BillingSummary>('/billing/sync', { method: 'POST', token }),

  setFrameLimit: (token: string, frameLimit: number) =>
    request<SubscriptionView>('/billing/quantity', { ...json({ frameLimit }, 'PUT'), token }),

  /* -------------------------------------------------------------- devices -- */

  listDevices: (token: string) => request<{ devices: DeviceSummary[] }>('/devices', { token }),

  createDevice: (token: string, label: string) =>
    request<{ id: string; label: string }>('/devices', { ...json({ label }), token }),

  getDevice: (token: string, id: string) => request<DeviceDetail>(`/devices/${id}`, { token }),

  renameDevice: (token: string, id: string, label: string) =>
    request<{ id: string; label: string }>(`/devices/${id}`, {
      ...json({ label }, 'PATCH'),
      token,
    }),

  deleteDevice: (token: string, id: string) =>
    request<void>(`/devices/${id}`, { method: 'DELETE', token }),

  leaveDevice: (token: string, id: string) =>
    request<void>(`/devices/${id}/membership`, { method: 'DELETE', token }),

  /** One-time upgrade path for a frame that predates accounts. */
  claimLegacyDevice: (token: string, secret: string) =>
    request<{ claimed: number }>('/devices/claim', { ...json({ secret }), token }),

  saveSettings: (token: string, id: string, settings: SlideshowSettings) =>
    request<SlideshowSettings>(`/devices/${id}/settings`, {
      ...json(settings, 'PUT'),
      token,
    }),

  saveSharing: (
    token: string,
    id: string,
    sharing: { publicUpload: boolean; autoAcceptEveryone: boolean },
  ) => request<DeviceSharing>(`/devices/${id}/sharing`, { ...json(sharing, 'PUT'), token }),

  rotatePublicLink: (token: string, id: string) =>
    request<{ publicUrl: string | null }>(`/devices/${id}/sharing/link`, {
      method: 'POST',
      token,
    }),

  /* -------------------------------------------------------------- sharing -- */

  listMembers: (token: string, id: string) =>
    request<{ members: DeviceMember[] }>(`/devices/${id}/members`, { token }),

  updateMember: (
    token: string,
    id: string,
    accountId: string,
    changes: { canSend?: boolean; autoAccept?: boolean; role?: 'delegate' | 'guest' },
  ) =>
    request<void>(`/devices/${id}/members/${accountId}`, {
      ...json(changes, 'PATCH'),
      token,
    }),

  removeMember: (token: string, id: string, accountId: string) =>
    request<void>(`/devices/${id}/members/${accountId}`, { method: 'DELETE', token }),

  listInvites: (token: string, id: string) =>
    request<{ invites: ShareInvite[] }>(`/devices/${id}/invites`, { token }),

  createInvite: (
    token: string,
    id: string,
    body: {
      label: string;
      role: 'delegate' | 'guest';
      canSend: boolean;
      autoAccept: boolean;
      ttlDays: number | null;
    },
  ) =>
    request<{ id: string; label: string; url: string }>(`/devices/${id}/invites`, {
      ...json(body),
      token,
    }),

  revokeInvite: (token: string, id: string, inviteId: string) =>
    request<void>(`/devices/${id}/invites/${inviteId}`, { method: 'DELETE', token }),

  previewInvite: (invite: string) =>
    request<InvitePreview>('/invites/preview', json({ invite })),

  acceptInvite: (token: string, invite: string) =>
    request<{ deviceId: string; alreadyIn: boolean }>('/invites/accept', {
      ...json({ invite }),
      token,
    }),

  /* -------------------------------------------------------------- screens -- */

  listScreens: (token: string, id: string) =>
    request<{ screens: Screen[] }>(`/devices/${id}/screens`, { token }),

  pairScreen: (token: string, id: string, code: string, label: string) =>
    request<{ ok: true; label: string }>(`/devices/${id}/screens`, {
      ...json({ code, label }),
      token,
    }),

  removeScreen: (token: string, id: string, screenId: string) =>
    request<void>(`/devices/${id}/screens/${screenId}`, { method: 'DELETE', token }),

  /* --------------------------------------------------------------- photos -- */

  listPhotos: (token: string, id: string, status: 'approved' | 'pending', before?: number) =>
    request<{ photos: ListedPhoto[]; nextCursor: number | null }>(
      `/devices/${id}/photos?status=${status}${before ? `&before=${before}` : ''}`,
      { token },
    ),

  uploadPhoto: (token: string, id: string, photo: PreparedPhoto, text: PhotoText) =>
    request<UploadResult>(`/devices/${id}/photos`, {
      method: 'POST',
      body: photoForm(photo, text),
      token,
    }),

  /**
   * Retype the caption and location on one photo on one frame. Both fields go
   * every time — an edit states what the photo says now, rather than patching
   * one line of it — so a cleared box really does clear.
   */
  editPhoto: (token: string, deviceId: string, photoId: string, text: PhotoText) =>
    request<PhotoText>(`/devices/${deviceId}/photos/${photoId}`, {
      ...json(text, 'PATCH'),
      token,
    }),

  /** One upload, several frames. The bytes cross the wire once. */
  uploadToFrames: (
    token: string,
    deviceIds: readonly string[],
    photo: PreparedPhoto,
    text: PhotoText,
  ) => {
    const form = photoForm(photo, text);
    form.set('deviceIds', JSON.stringify(deviceIds));
    return request<FanoutResult & { refused: string[] }>('/photos', {
      method: 'POST',
      body: form,
      token,
    });
  },

  /** Send pictures already in the library to more frames. Moves no bytes. */
  placeAssets: (token: string, assetIds: readonly string[], deviceIds: readonly string[]) =>
    request<{
      placed: Record<string, { sent: number; queued: number; already: number }>;
      refused: string[];
    }>('/photos/place', { ...json({ assetIds, deviceIds }), token }),

  listLibrary: (token: string, before?: number) =>
    request<{ assets: AssetView[]; nextCursor: number | null }>(
      `/photos/library${before ? `?before=${before}` : ''}`,
      { token },
    ),

  approvePhotos: (token: string, id: string, photoIds: readonly string[]) =>
    request<{ changed: number }>(`/devices/${id}/photos/approve`, {
      ...json({ photoIds }),
      token,
    }),

  rejectPhotos: (token: string, id: string, photoIds: readonly string[]) =>
    request<{ changed: number }>(`/devices/${id}/photos/reject`, {
      ...json({ photoIds }),
      token,
    }),

  deletePhoto: (token: string, id: string, photoId: string) =>
    request<void>(`/devices/${id}/photos/${photoId}`, { method: 'DELETE', token }),

  /* --------------------------------------------------------------- albums -- */

  listAlbums: (token: string) => request<{ albums: AlbumSummary[] }>('/albums', { token }),

  createAlbum: (token: string, label: string) =>
    request<{ id: string; label: string }>('/albums', { ...json({ label }), token }),

  getAlbum: (token: string, albumId: string) =>
    request<{ album: AlbumDetail; items: AlbumItemView[] }>(`/albums/${albumId}`, { token }),

  renameAlbum: (token: string, albumId: string, label: string) =>
    request<{ id: string; label: string }>(`/albums/${albumId}`, {
      ...json({ label }, 'PATCH'),
      token,
    }),

  deleteAlbum: (token: string, albumId: string) =>
    request<void>(`/albums/${albumId}`, { method: 'DELETE', token }),

  uploadToAlbum: (token: string, albumId: string, photo: PreparedPhoto, text: PhotoText) =>
    request<{ assetId: string; duplicate: boolean; pushed: ReconcileSummary[] }>(
      `/albums/${albumId}/photos`,
      { method: 'POST', body: photoForm(photo, text), token },
    ),

  addToAlbum: (token: string, albumId: string, assetIds: readonly string[]) =>
    request<{ added: number; pushed: ReconcileSummary[] }>(`/albums/${albumId}/items`, {
      ...json({ assetIds }),
      token,
    }),

  removeFromAlbum: (token: string, albumId: string, assetId: string) =>
    request<{ pushed: ReconcileSummary[] }>(`/albums/${albumId}/items/${assetId}`, {
      method: 'DELETE',
      token,
    }),

  /**
   * Retype an album item's text. Unlike `editPhoto` this reaches every frame the
   * album is published to — reconcile copies it down onto each placement — which
   * is why an album-owned photo cannot be edited on the frame itself.
   */
  editAlbumItem: (token: string, albumId: string, assetId: string, text: PhotoText) =>
    request<{ pushed: ReconcileSummary[] }>(`/albums/${albumId}/items/${assetId}`, {
      ...json(text, 'PATCH'),
      token,
    }),

  publishAlbum: (token: string, albumId: string, deviceId: string) =>
    request<{ published: ReconcileSummary | null; pushed: ReconcileSummary[] }>(
      `/albums/${albumId}/publications`,
      { ...json({ deviceId }), token },
    ),

  /** Works from either side: the album's owner, or the frame's. */
  unpublishAlbum: (token: string, albumId: string, deviceId: string) =>
    request<{ removed: number }>(`/albums/${albumId}/publications/${deviceId}`, {
      method: 'DELETE',
      token,
    }),

  listCarriedAlbums: (token: string, deviceId: string) =>
    request<{ albums: CarriedAlbum[] }>(`/devices/${deviceId}/albums`, { token }),

  /* --------------------------------------------------------------- public --
   * The slug travels in a body, never a path, so it stays out of access logs.
   * -------------------------------------------------------------------------- */

  publicFrame: (slug: string) => request<PublicFrame>('/public/frame', json({ slug })),

  publicUpload: (slug: string, name: string, photo: PreparedPhoto, text: PhotoText) => {
    const form = photoForm(photo, text);
    form.set('slug', slug);
    form.set('name', name);
    return request<UploadResult>('/public/photos', { method: 'POST', body: form });
  },
};
